import { Component, HostListener, computed, inject, signal } from '@angular/core';
import { RouterModule } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { AuthService } from '../../../auth/auth.service';

export interface StoreAccess {
  storeId: string;
  storeCode?: string;
  storeName?: string;
  locationId: string;
  locationName?: string;
  locationType?: string;
}

/**
 * Header chip showing the store/warehouse the user is working in, with a
 * searchable dropdown to switch. Switching reuses AuthService.setActiveStore
 * and reloads so every screen re-reads the active location — identical to the
 * behaviour of the previous header dropdown.
 */
@Component({
  selector: 'app-store-switcher',
  standalone: true,
  imports: [RouterModule],
  templateUrl: './store-switcher.component.html',
})
export class StoreSwitcherComponent {
  private authService = inject(AuthService);

  open = signal(false);
  query = signal('');

  private user = toSignal(this.authService.user$, { initialValue: null as any });
  access = computed<StoreAccess[]>(() => (this.user()?.storeAccess ?? []) as StoreAccess[]);

  private activeLocationId = signal(this.authService.getActiveLocationId());
  current = computed(() => {
    const id = this.activeLocationId();
    const list = this.access();
    return list.find((a) => a.locationId === id) ?? list[0] ?? null;
  });

  grouped = computed(() => {
    const q = this.query().trim().toLowerCase();
    const rows = this.access().filter(
      (a) =>
        !q ||
        `${a.storeCode ?? ''} ${a.storeName ?? ''} ${a.locationName ?? ''} ${a.locationType ?? ''}`
          .toLowerCase()
          .includes(q),
    );
    const order = ['Warehouse', 'Store'];
    const byType = new Map<string, StoreAccess[]>();
    for (const r of rows) {
      const t = r.locationType || 'Store';
      if (!byType.has(t)) byType.set(t, []);
      byType.get(t)!.push(r);
    }
    return Array.from(byType.entries())
      .sort(([a], [b]) => {
        const ia = order.indexOf(a);
        const ib = order.indexOf(b);
        return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
      })
      .map(([type, list]) => ({ type: type === 'Warehouse' ? 'Warehouses' : `${type}s`, rows: list }));
  });

  isCurrent(a: StoreAccess): boolean {
    return a.locationId === this.current()?.locationId;
  }

  toggle(event: MouseEvent): void {
    event.stopPropagation();
    if (this.access().length < 2) return;
    this.query.set('');
    this.open.update((o) => !o);
  }

  close(): void {
    this.open.set(false);
  }

  pick(a: StoreAccess): void {
    if (this.isCurrent(a)) {
      this.close();
      return;
    }
    this.authService.setActiveStore(a);
    this.activeLocationId.set(a.locationId);
    this.close();
    // Every screen reads the active location at load; a reload keeps them in sync.
    window.location.reload();
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    if (this.open()) this.close();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.close();
  }
}
